import React, { useState, useEffect } from "react";
import Paper from "@mui/material/Paper";
import Avatar from "@mui/material/Avatar";
import Tooltip from "@mui/material/Tooltip";
import { useDarkMode } from "../../context/darkModeContext";
import { useLocation } from "react-router-dom";

import Skeleton from "@mui/material/Skeleton";
import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";
import { db } from "../../helper/firebaseClient";
import useAuth from "../../stores/authStore";

const LatestActivities = () => {
  const { darkMode } = useDarkMode();
  const [selectedValue, setSelectedValue] = useState("all");
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const location = useLocation();
  const isUserProfile = location.pathname.includes("/profile");
  const profileData = useAuth((state) => state.profileData);
  // Attach realtime listener
  useEffect(() => {
    // org_id is the canonical field on the user doc; `org` is only kept
    // around as a legacy fallback (see backend requester_doc.get("org_id") or requester_doc.get("org")).
    const orgId = profileData?.org_id || profileData?.org;

    if (!orgId || typeof orgId !== "string") {
      setLogs([]);
      setLoading(false);
      setError("no-org");
      return;
    }

    setLoading(true);
    setError(null);

    // Must match the backend's org_access_logs() helper:
    //   org_ref(org_id).collection("accessLogs")
    // i.e. organizations/{orgId}/accessLogs — NOT access_logs/organizations/{orgId}.
    const logsRef = collection(db, "organizations", orgId, "accessLogs");

    const logsQuery = query(logsRef, orderBy("timestamp", "desc"), limit(25));

    const unsubscribe = onSnapshot(
      logsQuery,
      (snapshot) => {
        const nextLogs = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setLogs(nextLogs);
        setLoading(false);
        console.log("Logs:", nextLogs);
      },
      (error) => {
        console.error("LatestActivities snapshot error:", error);
        setLogs([]);
        setLoading(false);
        setError(error?.code || "snapshot-error");
      }
    );

    return () => {
      unsubscribe();
    };
  }, [profileData?.org_id, profileData?.org]);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "";

    const seconds = Number(timestamp?.seconds ?? 0);
    const nanos = Number(timestamp?.nanoseconds ?? 0);
    // Convert Firestore timestamp to milliseconds
    const milliseconds = seconds * 1000 + nanos / 1000000;

    const options = {
      timeZone: "Asia/Kolkata", // Adjust this to your desired time zone
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
    };

    // Return the formatted date string
    return new Date(milliseconds).toLocaleString("en-IN", options);
  };

  const handleSelectChange = (event) => {
    const value = event.target.value;
    setSelectedValue(value);
    console.log("Selected Value:", value);
  };

  const skeletons = Array.from({ length: 5 }, (_, i) => (
    <div
      key={i}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: "10px",
        padding: "9px 0",
        borderBottom: "1px solid #F9FAFB",
      }}
    >
      <Skeleton
        variant="circular"
        width={28}
        height={28}
        style={{ flexShrink: 0 }}
      />
      <div style={{ flex: 1 }}>
        <Skeleton width="65%" height={14} />
        <Skeleton width="40%" height={12} style={{ marginTop: 4 }} />
      </div>
    </div>
  ));

  return (
    <div
      style={{
        background: "#ffffff",
        borderRadius: "12px",
        border: "1px solid #EBEBEB",
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
        display: "flex",
        flexDirection: "column",
        height: "400px",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          flexShrink: 0,
          padding: "16px 20px 12px",
          borderBottom: "1px solid #F3F4F6",
          display: "flex",
          alignItems: "center",
          gap: "12px",
        }}
      >
        <span
          style={{
            fontSize: "14px",
            fontWeight: 700,
            color: "#1A1D1F",
            letterSpacing: "-0.01em",
          }}
        >
          Latest Activities
        </span>
        <select
          onChange={handleSelectChange}
          value={selectedValue}
          style={{
            fontSize: "12px",
            color: "#6F767E",
            background: "#F4F4F4",
            border: "1px solid #EBEBEB",
            borderRadius: "6px",
            padding: "3px 8px",
            outline: "none",
            cursor: "pointer",
            fontWeight: 500,
          }}
        >
          <option value="all">All</option>
          <option value="file_access">Access</option>
          <option value="download">Download</option>
        </select>
      </div>

      {/* Scrollable content */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "6px 0",
          scrollbarWidth: "thin",
          scrollbarColor: "#E5E7EB transparent",
        }}
      >
        {loading ? (
          <div style={{ padding: "0 14px" }}>{skeletons}</div>
        ) : error ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              padding: "0 20px",
              textAlign: "center",
              color: "#9CA3AF",
              fontSize: "12.5px",
            }}
          >
            {error === "no-org"
              ? "We couldn't find your organization on this account."
              : error === "permission-denied"
                ? "You don't have permission to view this activity."
                : "Couldn't load activity right now."}
          </div>
        ) : logs.filter(
            (log) => selectedValue === "all" || log.event === selectedValue
          ).length ? (
          logs
            .filter(
              (log) => selectedValue === "all" || log.event === selectedValue
            )
            .map((log) => (
              <div
                key={log.id}
                style={{
                  display: "flex",
                  gap: "10px",
                  alignItems: "flex-start",
                  padding: "9px 20px",
                  borderBottom: "1px solid #F9FAFB",
                  transition: "background 0.12s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "#FAFAFA")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "transparent")
                }
              >
                <Tooltip title={log.username || log.user_email} arrow>
                  <Avatar
                    src={log.profilePic}
                    alt="owner pic"
                    sx={{ width: 28, height: 28, flexShrink: 0, mt: "2px" }}
                  />
                </Tooltip>
                <div>
                  <p
                    style={{
                      fontSize: "13px",
                      color: "#374151",
                      lineHeight: 1.5,
                      margin: 0,
                    }}
                  >
                    <span style={{ fontWeight: 600, color: "#111827" }}>
                      {log.username || log.user || "User"}
                    </span>{" "}
                    {log.event === "screenshot"
                      ? "took screenshot of"
                      : log.event === "download"
                        ? "downloaded"
                        : log.event === "edit"
                          ? "edited"
                          : "accessed"}{" "}
                    <span style={{ fontWeight: 600, color: "#111827" }}>
                      {(log.file_name || "").toString().split("_TS=")[0] ||
                        "a file"}
                    </span>
                  </p>
                  <p
                    style={{
                      fontSize: "11px",
                      color: "#9CA3AF",
                      marginTop: "3px",
                    }}
                  >
                    {formatTimestamp(log.timestamp)}
                  </p>
                </div>
              </div>
            ))
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              color: "#9CA3AF",
              fontSize: "12.5px",
            }}
          >
            No recent activity yet
          </div>
        )}
      </div>
    </div>
  );
};

export default LatestActivities;
